import { Injectable } from '@angular/core';
import { CustomProductList } from '@catalog/const';
import {
  CustomBasePageMetaResolver,
  PageCategoryBreadcrumbsResolver,
  PageSeoTextResolver,
  PageSubTitleResolver,
} from '@core/feature';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SeoFacetsService } from '@seo/feature';
import {
  BreadcrumbMeta,
  CanonicalPageResolver,
  PageBreadcrumbResolver,
  PageDescriptionResolver,
  PageHeadingResolver,
  PageMetaResolver,
  PageRobotsMeta,
  PageRobotsResolver,
  PageTitleResolver,
  PageType,
  PageTypeResolver,
  ProductSearchPage,
  RouterState,
  RoutingService,
  StringTemplate,
} from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { CategoryDetailsService, ProductListService } from '../services';
import { BaseProductListPageMetaResolver } from './base-product-list-page-meta.resolver';

@Injectable({
  providedIn: 'root',
})
export class CategoryPageMetaResolver
  extends PageMetaResolver
  implements
    PageTitleResolver,
    PageHeadingResolver,
    PageSubTitleResolver,
    PageBreadcrumbResolver,
    PageCategoryBreadcrumbsResolver,
    PageSeoTextResolver,
    PageDescriptionResolver,
    PageRobotsResolver,
    CanonicalPageResolver,
    PageTypeResolver
{
  private activeCategorySeo$ = this.categoryDetailsService.getSeo().pipe(filter(Boolean));

  constructor(
    private seoFacetsService: SeoFacetsService,
    private basePageMetaResolver: CustomBasePageMetaResolver,
    private categoryDetailsService: CategoryDetailsService,
    private baseProductListPageMetaResolver: BaseProductListPageMetaResolver,
    private routingService: RoutingService,
    private productListService: ProductListService,
  ) {
    super();
    this.pageType = PageType.CATEGORY_PAGE;
  }

  resolveLoading(): Observable<boolean> {
    return combineLatest({
      categoryLoading: this.categoryDetailsService.getCategoryDetailsLoading(),
      productListLoading: this.productListService.getLoading(),
    }).pipe(map(({ categoryLoading, productListLoading }) => categoryLoading || productListLoading));
  }

  resolveTitle(): Observable<string> {
    return combineLatest({
      metaTitle: combineLatest({
        activeCategorySeo: this.activeCategorySeo$,
        seoFacetText: this.seoFacetsService.getSeoFacetText(),
      }).pipe(
        map(({ activeCategorySeo, seoFacetText }) =>
          seoFacetText
            ? this.processSeoFacetsTemplate(activeCategorySeo.metaFilterTitle, seoFacetText)
            : activeCategorySeo.metaTitle,
        ),
      ),
      currentBreadcrumbLabel: this.getCurrentBreadcrumbLabel(),
    }).pipe(
      map(({ metaTitle, currentBreadcrumbLabel }) => metaTitle || currentBreadcrumbLabel),
      switchMap((text) => this.baseProductListPageMetaResolver.processSeoTemplate(text)),
    );
  }

  resolveHeading(): Observable<string> {
    return this.getCurrentBreadcrumbLabel();
  }

  resolveSubTitle(): Observable<string> {
    return this.baseProductListPageMetaResolver.getSubTitle();
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest({
      p: this.baseProductListPageMetaResolver.getSearchResults(),
      routerState: this.routingService.getRouterState(),
    }).pipe(
      switchMap(({ p, routerState }: { p: ProductSearchPage; routerState: RouterState }) => {
        return p.breadcrumbs ? this.getBreadcrumbData(p, 'Главная', routerState.state.semanticRoute) : of(null);
      }),
    );
  }

  resolveCategoryBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return this.resolveBreadcrumbs().pipe(map((breadcrumbs?: BreadcrumbMeta[]) => breadcrumbs?.slice(1) || []));
  }

  resolveSeoText(): Observable<string> {
    return combineLatest({
      activeCategorySeo: this.activeCategorySeo$,
      seoFacetText: this.seoFacetsService.getSeoFacetText(),
    }).pipe(
      map(({ activeCategorySeo, seoFacetText }) =>
        seoFacetText
          ? this.processSeoFacetsTemplate(activeCategorySeo.seoFilterText, seoFacetText)
          : activeCategorySeo.seoText,
      ),
      switchMap((text) => this.baseProductListPageMetaResolver.processSeoTemplate(text)),
    );
  }

  resolveDescription(): Observable<string | undefined> {
    return combineLatest({
      activeCategorySeo: this.activeCategorySeo$,
      seoFacetText: this.seoFacetsService.getSeoFacetText(),
    }).pipe(
      map(({ activeCategorySeo, seoFacetText }) =>
        seoFacetText
          ? this.processSeoFacetsTemplate(activeCategorySeo.metaFilterDescription, seoFacetText)
          : activeCategorySeo.metaDescription,
      ),
      switchMap((text) => this.baseProductListPageMetaResolver.processSeoTemplate(text)),
    );
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.basePageMetaResolver.resolveRobots();
  }

  resolveCanonicalUrl(): Observable<string> {
    return this.basePageMetaResolver.resolveCanonicalUrl();
  }

  resolveType(): Observable<string> {
    return this.basePageMetaResolver.resolveType();
  }

  private getCurrentBreadcrumbLabel(): Observable<string> {
    return this.resolveBreadcrumbs().pipe(
      map((crumbs: BreadcrumbMeta[]) => {
        return crumbs?.slice(-1)[0]?.label;
      }),
    );
  }

  private getBreadcrumbData(
    page: ProductSearchPage,
    label: string,
    semanticRoute: string,
  ): Observable<BreadcrumbMeta[]> {
    const breadcrumbs: BreadcrumbMeta[] = [];
    breadcrumbs.push({ label, link: '/' });

    for (const br of page.breadcrumbs) {
      if (semanticRoute === CustomProductList.SaleProductList && br.facetCode === 'sale') {
        breadcrumbs.push({
          label: br.facetName,
          link: `/sale/`,
        });
      } else if (semanticRoute === CustomProductList.BestOffersProductList && br.facetCode === 'hasDiscount') {
        breadcrumbs.push({
          label: br.facetName,
          link: `/best-offers/`,
        });
      }
    }

    const parentPage = breadcrumbs[breadcrumbs.length - 1];

    for (const br of page.breadcrumbs) {
      if (br.facetCode === 'category' || br.facetCode === 'allCategories') {
        let link = `/category/${br.facetValueCode}/`;

        if (parentPage.link !== '/') {
          link = `${parentPage.link}${br.facetValueCode}/`;
        }

        breadcrumbs.push({
          link,
          label: br.facetValueName,
        });
      }
    }

    return of(breadcrumbs);
  }

  private processSeoFacetsTemplate(template: string, seoFacetText: string): string {
    return StringTemplate.resolve(template, {
      facetFilters: seoFacetText,
    });
  }
}
